/**
 * StorefrontPhonePreview — Floating phone-frame preview of a live storefront product.
 *
 * Opens over the dashboard as a draggable phone simulator. Shows the REAL
 * storefront product page in a 375px-wide iframe (mobile viewport).
 * Desktop only — on mobile, links open in a new tab as usual.
 *
 * The "wow" factor: user stays in the dashboard while seeing their product
 * live on their actual store. Like iPhone Simulator on Mac.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { X, ExternalLink, Smartphone } from "lucide-react";
import { createPortal } from "react-dom";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";

type Props = {
  url: string;
  productName: string;
  onClose: () => void;
  language: string;
};

const PHONE_WIDTH = 360;
const PHONE_HEIGHT = 740;
const HEADER_HEIGHT = 44;
const FRAME_PADDING = 4;

export function StorefrontPhonePreview({ url, productName, onClose, language }: Props) {
  const [pos, setPos] = useState(() => ({
    x: Math.max(40, window.innerWidth - PHONE_WIDTH - FRAME_PADDING * 2 - 60),
    y: Math.max(40, Math.round((window.innerHeight - PHONE_HEIGHT - HEADER_HEIGHT - FRAME_PADDING * 2) / 2)),
  }));
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const frameRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button, a")) return;
    dragging.current = true;
    dragOffset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [pos.x, pos.y]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    setPos({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y,
    });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  // Close on ESC
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const totalWidth = PHONE_WIDTH + FRAME_PADDING * 2;

  return createPortal(
    <>
      {/* Backdrop — subtle, click to close */}
      <div
        className="fixed inset-0 bg-black/20 z-[9998] backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Phone frame */}
      <div
        ref={frameRef}
        className="fixed z-[9999] select-none rounded-[1.25rem] shadow-2xl ring-1 ring-black/[0.08]"
        style={{
          left: pos.x,
          top: pos.y,
          width: totalWidth,
        }}
      >
        {/* Header bar — draggable */}
        <div
          className="flex items-center justify-between px-3 py-2 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-700 rounded-t-[1.25rem] cursor-grab active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Smartphone className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400 shrink-0" />
            <span className="text-[12px] font-medium text-neutral-700 dark:text-neutral-300 truncate">{productName}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
              title={language === "th" ? "เปิดในแท็บใหม่" : "Open in new tab"}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Phone body */}
        <div
          className="bg-neutral-100 dark:bg-neutral-800 rounded-b-[1.25rem] overflow-hidden"
          style={{ padding: FRAME_PADDING, paddingTop: 0 }}
        >
          {/* Screen */}
          <div
            className="relative bg-white rounded-[0.75rem] overflow-hidden"
            style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT }}
          >
            {/* Loading state — branded starburst animation */}
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <AnimatedStarburst size={40} />
              </div>
            )}
            <iframe
              src={url}
              title={productName}
              className="border-0"
              style={{ width: PHONE_WIDTH, height: PHONE_HEIGHT }}
              onLoad={() => setLoaded(true)}
            />
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
